import { useEffect, useRef, useState } from "react";

const modeOptions = [
  { value: "general", label: "일반 지식" },
  { value: "dev", label: "개발 지식" },
];

function KnowledgeBasePanel({ onLoad, onAddText, onAddFile, onDelete, onChanged }) {
  const fileRef = useRef(null);
  const [mode, setMode] = useState("general");
  const [query, setQuery] = useState("");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [fileName, setFileName] = useState("");
  const [busy, setBusy] = useState("");
  const [message, setMessage] = useState("");
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");

  const loadItems = async () => {
    setBusy("load");
    setMessage("");
    try {
      const data = await onLoad({ mode, query, limit: 100 });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (err) {
      setMessage(err.message);
    } finally {
      setBusy("");
    }
  };

  useEffect(() => {
    loadItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const handleAddText = async () => {
    if (!content.trim()) {
      setMessage("등록할 지식 내용을 입력하세요.");
      return;
    }
    setBusy("add-text");
    setMessage("");
    try {
      const result = await onAddText({ title: title || "관리자 등록 지식", content, mode, visibility: "company" });
      setTitle("");
      setContent("");
      setMessage(`${result.chunks || 0}개 조각으로 지식을 등록했습니다.`);
      await loadItems();
      onChanged?.();
    } catch (err) {
      setMessage(err.message);
    } finally {
      setBusy("");
    }
  };

  const handleAddFile = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) {
      setMessage("등록할 파일을 선택하세요.");
      return;
    }
    setBusy("add-file");
    setMessage("");
    try {
      const result = await onAddFile(file, mode, "company");
      setFileName("");
      if (fileRef.current) fileRef.current.value = "";
      setMessage(`${result.chunks || 0}개 조각으로 파일 지식을 등록했습니다.`);
      await loadItems();
      onChanged?.();
    } catch (err) {
      setMessage(err.message);
    } finally {
      setBusy("");
    }
  };

  const handleDelete = async (item) => {
    setPendingDelete(item);
    setDeleteConfirmText("");
    setMessage("");
  };

  const executeDelete = async () => {
    if (!pendingDelete?.id) return;
    if (deleteConfirmText !== "DELETE") {
      setMessage("삭제하려면 DELETE를 정확히 입력하세요.");
      return;
    }
    const item = pendingDelete;
    setBusy(`delete:${item.id}`);
    setMessage("");
    try {
      const result = await onDelete(item.id, mode);
      setPendingDelete(null);
      setDeleteConfirmText("");
      setMessage(`${result.deleted?.length || 1}개 지식 조각을 삭제했습니다.`);
      await loadItems();
      onChanged?.();
    } catch (err) {
      setMessage(err.message);
    } finally {
      setBusy("");
    }
  };

  return (
    <section className="knowledge-panel">
      <div className="admin-panel-header">
        <div>
          <p className="panel-kicker">Knowledge Base</p>
          <h3>지식베이스 관리</h3>
          <p>운영 챗봇이 검색하는 내부 지식을 직접 등록하고 불필요한 항목을 삭제합니다.</p>
        </div>
        <button className="admin-secondary-button" onClick={loadItems} disabled={busy === "load"}>
          {busy === "load" ? "불러오는 중..." : "새로고침"}
        </button>
      </div>

      {message && <div className={message.includes("실패") || message.includes("못했습니다") ? "admin-error" : "admin-success"}>{message}</div>}
      {pendingDelete && (
        <div className="knowledge-confirm">
          <div>
            <strong>{pendingDelete.title || pendingDelete.id}</strong>
            <span>같은 원본에서 만들어진 지식 조각이 함께 삭제됩니다. 계속하려면 DELETE를 입력하세요.</span>
          </div>
          <input value={deleteConfirmText} onChange={(event) => setDeleteConfirmText(event.target.value)} placeholder="DELETE" />
          <button className="admin-danger-button" onClick={executeDelete} disabled={busy === `delete:${pendingDelete.id}`}>삭제</button>
          <button className="admin-secondary-button" onClick={() => setPendingDelete(null)} disabled={!!busy}>취소</button>
        </div>
      )}

      <div className="knowledge-toolbar">
        <select value={mode} onChange={(event) => setMode(event.target.value)}>
          {modeOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
        </select>
        <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="제목, 내용, 회사 검색" />
        <button className="admin-secondary-button" onClick={loadItems} disabled={busy === "load"}>검색</button>
        <span>총 {total}개</span>
      </div>

      <div className="knowledge-editor-grid">
        <div className="knowledge-editor">
          <h4>텍스트 추가</h4>
          <input value={title} onChange={(event) => setTitle(event.target.value)} placeholder="제목" />
          <textarea value={content} onChange={(event) => setContent(event.target.value)} placeholder="등록할 지식 내용을 입력하세요." rows={8} />
          <button className="admin-approve-button" onClick={handleAddText} disabled={busy === "add-text"}>
            {busy === "add-text" ? "등록 중..." : "텍스트 지식 추가"}
          </button>
        </div>

        <div className="knowledge-editor">
          <h4>파일 추가</h4>
          <label className="document-file-picker">
            <input
              ref={fileRef}
              type="file"
              accept=".txt,.md,.json,.csv,.pdf,.doc,.docx,.xlsx,.hwpx"
              onChange={(event) => setFileName(event.target.files?.[0]?.name || "")}
            />
            <span>{fileName || "지식 파일 선택"}</span>
          </label>
          <p className="knowledge-hint">파일에서 텍스트를 추출해 지식베이스에 저장합니다.</p>
          <button className="admin-approve-button" onClick={handleAddFile} disabled={busy === "add-file"}>
            {busy === "add-file" ? "등록 중..." : "파일 지식 추가"}
          </button>
        </div>
      </div>

      <div className="knowledge-list">
        {items.length === 0 && <div className="empty-panel">표시할 지식 항목이 없습니다.</div>}
        {items.map((item) => (
          <article className="knowledge-item" key={item.id}>
            <div>
              <strong>{item.title}</strong>
              <span>{item.company || "회사 없음"} · {item.visibility || "company"} · {item.chars}자</span>
              <p>{item.preview || "내용 미리보기가 없습니다."}</p>
            </div>
            <button className="admin-danger-button" onClick={() => handleDelete(item)} disabled={busy === `delete:${item.id}`}>
              {busy === `delete:${item.id}` ? "삭제 중..." : "삭제"}
            </button>
          </article>
        ))}
      </div>
    </section>
  );
}

export default KnowledgeBasePanel;
